import {
  addMonths, differenceInMonths, endOfMonth, startOfMonth, subMonths,
} from 'date-fns';
import { GetServerSidePropsContext } from 'next';
import newTZDate, { formatDate, todayDate } from 'src/common/utils/dateHelpers';
import ensureString from 'src/common/utils/ensureString';
import { RecentInquiryData } from 'src/common/utils/recentInquiryCookieData';
import captureException from 'src/common/utils/captureException';
import serverApiFetch from 'src/common/utils/serverApiFetch';
import isApiResponseError from 'src/common/utils/isApiResponseError';
import { throwApiErrors } from 'src/common/utils/handleApiErrors';

type Params = {
  listing_id: string;
};

export type Context = Pick<GetServerSidePropsContext<Params>, 'req' | 'params'>;

/**
 * If this listing is enabled for Calendar Pricing, this will always return no Instabooks,
 * unless isDirectInstabook is true
 */
type isDirectInstabook = boolean | undefined;

const getInstabookDates = async (
  context: Context,
  listingId: string,
  startDate: string,
  endDate: string,
  isDirectInstabook: isDirectInstabook = false,
): Promise<string[]> => {
  let dates: string[] = [];
  const params = new URLSearchParams({
    boat_id: listingId,
    start_date: startDate,
    end_date: endDate,
  });
  if (isDirectInstabook) {
    params.append('is_direct_booking', 'true');
  }
  const url = `/api/v4/instabook/availability_dates_only/?${params.toString()}`;
  try {
    const response = await serverApiFetch<string[]>(context, url);
    dates = response;
  } catch (err) {
    if ([400, 401, 403, 404, 500, 502, 504].some((status) => isApiResponseError(err, status))) {
      // Don't spam sentry with errors
    } else if (throwApiErrors(err)) {
      // throws in throwApiErrors
    } else {
      captureException(err);
    }
  }
  return dates;
};

export const getInitialCachedDates = (defaultCookieData?: Partial<RecentInquiryData>): string[] => {
  const dates = defaultCookieData?.dates || [];
  const today = todayDate();
  let defaultDate = today;

  if (dates && dates.length) {
    defaultDate = newTZDate(dates[0]);
  }

  // Get difference of current month and month of default date.
  const diffOfMonths = differenceInMonths(startOfMonth(defaultDate), startOfMonth(today));
  let apiDates: string[] = [];
  const startMonthDate = startOfMonth(defaultDate);
  if (diffOfMonths >= 1) {
    // if difference of month is more than 1, then get 3 months data. i.e.
    // previous month, current month and next month
    apiDates = [
      formatDate(subMonths(startMonthDate, 1)),
      formatDate(startMonthDate),
      formatDate(addMonths(startMonthDate, 1)),
    ];
  } else {
    apiDates = [
      formatDate(startMonthDate),
      formatDate(addMonths(startMonthDate, 1)),
    ];
  }

  return apiDates;
};

const serverGetInstabookDates = async (
  context: Context,
  dates: string[] = [],
  /**
   * If this listing is enabled for Calendar Pricing, this will always return no Instabooks,
   * unless isDirectInstabook is true
   */
  isDirectInstabook: isDirectInstabook = false,
): Promise<string[]> => {
  const listingId = ensureString(context?.params?.listing_id);
  let instabookDates: string[] = [];
  const promises: Promise<void>[] = [];

  dates.forEach((date) => {
    const start = startOfMonth(new Date(date));
    const end = endOfMonth(new Date(date));
    promises.push(
      getInstabookDates(context, listingId, formatDate(start), formatDate(end), isDirectInstabook)
        .then((data) => {
          instabookDates = instabookDates.concat(data);
        }),
    );
  });
  await Promise.all(promises);

  return instabookDates;
};

export default serverGetInstabookDates;
